import { DataManagerFactory } from 'utils/MobxDataManager/dataManagerFactory';
import { EligibilityProrationOverrideStore } from 'nomst/Stores/Decision/eligibilityProrationOverrideStore';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { EmployeeGet } from 'nomst/Stores/Utility/employeeGet';
import { TeamDecisionStore } from 'nomst/Stores/Decision/teamDecision.store';
import { DecisionStore } from 'nomst/Stores/Decision/decision.store';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { ChangeHistoryStore } from 'nomst/Stores/ChangeHistory/changeHistory.store';
import { NavigateToNoMst } from 'nomst/Stores/Navigation/navigateToNoMst';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import { history } from 'nomst/Stores/Navigation/router.store';
import { ConfigurationsStore } from 'nomst/Stores/Configuration/configurations.store';
import * as workflowSettings from 'nomst/Actions/workflowSettings';
import { ManagerListSidePanelStore } from 'nomst/Stores/Decision/ManagerListSidePanel/managerListSidePanel.store';
import { User } from 'nomst/Stores/User/user';
import { ConfigurationDataManager } from 'utils/MobxDataManager/configurationDataManager';
import { WorkflowSettingsViewStore } from 'nomst/Stores/Configuration/workflowSettingsView.store';
import { CycleRules } from 'utils/cycleRules';
import { ErrorMessages } from 'nomst/Stores/Utility/errorMessages';
import { TeamErrorData } from 'nomst/Stores/Utility/teamErrorData';
import { ContactFormHelper } from 'nomst/Stores/Utility/contactFormHelper';
import { BudgetConfigurationViewStore } from 'nomst/Stores/Budget/budgetConfigurationView.store';
import { EditBudgetViewStore } from 'nomst/Stores/Budget/editBudgetView.store';
import SalesForceDataManager from 'utils/MobxDataManager/salesForceDataManager';
import { NotificationStore } from 'nomst/Stores/Notification/notification.store';
import { EventStore } from 'nomst/Stores/Event/event.store';
import { JobCollabEmployees } from 'nomst/Stores/JobCollab/JobCollabEmployees';
import { JobStore } from 'nomst/Stores/JobCollab/jobs';
import CompMemoStore from 'nomst/Stores/Decision/compMemoStore';

let dataManagerFactory: DataManagerFactory;
let eligibilityOverride: EligibilityProrationOverrideStore;
let getEmployees: EmployeeGet;
let currencyConversion: CurrencyConversion;
let changeHistoryStore: ChangeHistoryStore;
let navigateToNoMst: NavigateToNoMst;
let mobxTeamStore: MobxTeamStore;
let configurationsStore: ConfigurationsStore;
let managerListSidePanelStore: ManagerListSidePanelStore;
let workflowSettingsViewStore: WorkflowSettingsViewStore;
let cycleRules: CycleRules;
let errorMessages: ErrorMessages;
let teamErrorData: TeamErrorData;
let contactFormHelper: ContactFormHelper;
let budgetConfigurationViewStore: BudgetConfigurationViewStore;
let editBudgetViewStore: EditBudgetViewStore;
let notificationStore: NotificationStore;
let eventStore: EventStore;
let compMemoStore: CompMemoStore;
let maid: string;
let employeeList: JobCollabEmployees;
let jobStore: JobStore;

// initializes all of our singleton objects
const init = (
    configuration: Configuration,
    decisionStore: DecisionStore,
    navigation: NavigationStore,
    user: User,
    masterAccountId?: string): void => {
    eligibilityOverride = new EligibilityProrationOverrideStore(configuration);
    getEmployees = new EmployeeGet(decisionStore.teamDecisionStore);
    currencyConversion = new CurrencyConversion(configuration.currencies);
    navigateToNoMst = new NavigateToNoMst(navigation);
    changeHistoryStore = new ChangeHistoryStore();
    managerListSidePanelStore = new ManagerListSidePanelStore(configuration, decisionStore.managerWorkflowStore, user);
    workflowSettingsViewStore = new WorkflowSettingsViewStore();
    workflowSettings.init(workflowSettingsViewStore);
    cycleRules = new CycleRules(user, decisionStore.managerWorkflowStore);
    errorMessages = new ErrorMessages();
    teamErrorData = new TeamErrorData(masterAccountId as string, user.userId);
    contactFormHelper = new ContactFormHelper();
    notificationStore = new NotificationStore(masterAccountId ?? '');
    eventStore =  new EventStore();
    employeeList = new JobCollabEmployees(masterAccountId ?? '', configuration);
    jobStore = new JobStore(masterAccountId ?? '');
    compMemoStore = new CompMemoStore(configuration);
    maid = masterAccountId ?? '';
};

const initDataManager = (): void => {
    dataManagerFactory = new DataManagerFactory();
};

const initEditBudgetViewStore = (): void => {
    editBudgetViewStore =  new EditBudgetViewStore();
};

const initHelpArticleSingletons = (sfDataManager: SalesForceDataManager): void => {
    dataManagerFactory = new DataManagerFactory();
    dataManagerFactory.setSalesForceDataManager(sfDataManager);
    contactFormHelper = new ContactFormHelper();
};

// For testing purposes only
const initConfigurationsStore = (configsStore: ConfigurationsStore): void => {
    configurationsStore = configsStore;
};

// For testing purposes only
const initBudgetConfiguraionViewStore = (store: BudgetConfigurationViewStore): void => {
    budgetConfigurationViewStore = store;
};

// updates MST to MOBX without destroying urls and flags that we get on boot
const updateSingletons = (
    configuration: Configuration,
    team: TeamDecisionStore,
    navigation: NavigationStore): void => {
    eligibilityOverride = new EligibilityProrationOverrideStore(configuration);
    getEmployees = new EmployeeGet(team);
    currencyConversion = new CurrencyConversion(configuration.currencies);
    navigateToNoMst.updateNavStore(navigation);
    managerListSidePanelStore.updateConfiguration(configuration);
};

// initial creation of the store
const setupMobxTeamStore
        = async (
            mobxUser: User,
            configurationDataManager: ConfigurationDataManager,
            masterAccountId: string,
            userId: string,
            accessToken: string): Promise<void> => {
            configurationsStore = new ConfigurationsStore(masterAccountId, userId, accessToken);

            const configTasks = [
                configurationsStore.fetchConfiguration(),
                configurationsStore.fetchCurrentCycleConfiguration()
            ];
            if (mobxUser.isHrOrSupport) {
                configTasks.push(configurationsStore.fetchCycleConfigurations());
            }

            await Promise.all(configTasks);

            const mobxConfiguration = configurationsStore.currentConvertedConfiguration;

            const hrAssistant =
    await configurationDataManager.getHrAssistantUser(
        masterAccountId,
        mobxConfiguration.cycleId,
        mobxUser.userId);

            if (hrAssistant) {
                mobxUser.setAsHrAssistant();
            }
            mobxTeamStore = new MobxTeamStore(
                history,
                mobxUser);

            budgetConfigurationViewStore = new BudgetConfigurationViewStore(configurationsStore, mobxConfiguration.format);
            editBudgetViewStore = new EditBudgetViewStore();
        };

// this one updates the store but doesn't wipe it out
const updateMobxTeamStore = async (): Promise<void> => {
    const mobxConfiguration = configurationsStore.currentConvertedConfiguration;

    updateSingletons(
        mobxConfiguration,
        mobxTeamStore.decisionStore.teamDecisionStore,
        mobxTeamStore.navigationStore
    );

    configurationsStore.clearCycleconfigurations();
    await Promise.all([
        configurationsStore.fetchConfiguration(),
        configurationsStore.fetchCycleConfigurations()
    ]);

    await workflowSettingsViewStore.initData();
};

const fetchConfigurations = async (): Promise<void> => {
    configurationsStore.clearCycleconfigurations();
    await Promise.all([
        configurationsStore.fetchConfiguration(),
        configurationsStore.fetchCycleConfigurations()
    ]);
};

// Updates the current configuration for all stores that need it
const updateConfiguration = (): void => {
    mobxTeamStore.updateConfiguration();
    const configuration = configurationsStore.currentConvertedConfiguration;
    eligibilityOverride = new EligibilityProrationOverrideStore(configuration);
    currencyConversion = new CurrencyConversion(configuration.currencies);
    managerListSidePanelStore.updateConfiguration(configuration);
};

export {
    init,
    initHelpArticleSingletons,
    initDataManager,
    initConfigurationsStore,
    dataManagerFactory,
    eligibilityOverride,
    getEmployees,
    currencyConversion,
    changeHistoryStore,
    navigateToNoMst,
    setupMobxTeamStore,
    mobxTeamStore,
    updateMobxTeamStore,
    updateConfiguration,
    configurationsStore,
    managerListSidePanelStore,
    workflowSettingsViewStore,
    cycleRules,
    errorMessages,
    teamErrorData,
    contactFormHelper,
    budgetConfigurationViewStore,
    editBudgetViewStore,
    fetchConfigurations,
    maid,
    initEditBudgetViewStore,
    initBudgetConfiguraionViewStore,
    notificationStore,
    eventStore,
    employeeList,
    jobStore,
    compMemoStore
};
